if (!customElements.get('content-share-popover')) {
  customElements.define(
    'content-share-popover',
    class ContentSharePopover extends HTMLElement {
      connectedCallback() {
        this.shareContainer = this.querySelector('.content-share-popover__inner');
        this.shareButton = this.querySelector('[data-share-popover-button]');
        this.sharePopover = this.querySelector('[data-share-popover-menu]');
        this.shareLinks = Array.from(this.querySelectorAll('[data-share-link]'));
        this.copyLinkButtons = Array.from(this.querySelectorAll('[data-share-copy-link]'));
        this.status = this.querySelector('[data-share-status]');
        this.desktopShareQuery = window.matchMedia('(min-width: 990px)');
        this.shareAnimationDuration = 300;
        this.shareAnimationFrame = null;
        this.shareAnimationTimeout = null;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onShareQueryChange = this.onShareQueryChange.bind(this);

        this.updateShareLinks();

        if (this.shareButton) {
          this.shareButton.addEventListener('click', () => this.onShareButtonClick());
        }

        this.copyLinkButtons.forEach((button) => {
          button.addEventListener('click', () => this.copyCurrentLink());
        });

        if (this.desktopShareQuery.addEventListener) {
          this.desktopShareQuery.addEventListener('change', this.onShareQueryChange);
        } else if (this.desktopShareQuery.addListener) {
          this.desktopShareQuery.addListener(this.onShareQueryChange);
        }
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.shareAnimationFrame);
        clearTimeout(this.shareAnimationTimeout);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);

        if (this.desktopShareQuery.addEventListener) {
          this.desktopShareQuery.removeEventListener('change', this.onShareQueryChange);
        } else if (this.desktopShareQuery.removeListener) {
          this.desktopShareQuery.removeListener(this.onShareQueryChange);
        }
      }

      async shareContent() {
        const shareData = {
          title: this.currentShareTitle,
          url: this.currentShareUrl,
        };

        if (navigator.share) {
          try {
            await navigator.share(shareData);
            this.setStatus(this.dataset.shareSuccess || 'Shared');
            return;
          } catch (error) {
            if (error.name === 'AbortError') return;
          }
        }

        await this.copyToClipboard(shareData.url);
        this.setStatus(this.dataset.copySuccess || 'Link copied');
      }

      onShareButtonClick() {
        if (this.sharePopover && this.desktopShareQuery.matches) {
          this.toggleSharePopover();
          return;
        }

        this.shareContent();
      }

      async copyCurrentLink() {
        await this.copyToClipboard(this.currentShareUrl);
        this.setStatus(this.dataset.copySuccess || 'Link copied');
        this.closeSharePopover();
      }

      toggleSharePopover() {
        if (!this.sharePopover) return;

        if (this.sharePopover.hidden || this.sharePopover.classList.contains('is-closing')) {
          this.openSharePopover();
        } else {
          this.closeSharePopover();
        }
      }

      openSharePopover() {
        cancelAnimationFrame(this.shareAnimationFrame);
        clearTimeout(this.shareAnimationTimeout);
        this.updateShareLinks();
        this.sharePopover.hidden = false;
        this.sharePopover.classList.remove('is-closing');
        this.shareAnimationFrame = requestAnimationFrame(() => {
          this.sharePopover.classList.add('is-open');
        });
        this.shareButton.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      closeSharePopover() {
        if (!this.sharePopover || this.sharePopover.hidden) return;

        cancelAnimationFrame(this.shareAnimationFrame);
        clearTimeout(this.shareAnimationTimeout);
        this.sharePopover.classList.remove('is-open');
        this.sharePopover.classList.add('is-closing');
        this.shareButton.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);

        this.shareAnimationTimeout = setTimeout(() => {
          this.sharePopover.hidden = true;
          this.sharePopover.classList.remove('is-closing');
        }, this.shareAnimationDuration);
      }

      onDocumentClick(event) {
        if (this.shareContainer && this.shareContainer.contains(event.target)) return;

        this.closeSharePopover();
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;

        this.closeSharePopover();
        this.shareButton.focus();
      }

      onShareQueryChange(event) {
        if (!event.matches) {
          this.closeSharePopover();
        }
      }

      updateShareLinks() {
        const url = encodeURIComponent(this.currentShareUrl);
        const title = encodeURIComponent(this.currentShareTitle);

        this.shareLinks.forEach((link) => {
          const type = link.dataset.shareLink;

          if (type === 'email') {
            link.href = `mailto:?subject=${title}&body=${url}`;
          } else if (type === 'x') {
            link.href = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
          } else if (type === 'linkedin') {
            link.href = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
          } else if (type === 'facebook') {
            link.href = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
          }
        });
      }

      get currentShareUrl() {
        return this.dataset.shareUrl || window.location.href;
      }

      get currentShareTitle() {
        return this.dataset.shareTitle || document.title;
      }

      async copyToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return;
        }

        const input = document.createElement('input');
        input.value = text;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      setStatus(message) {
        if (!this.status) return;

        this.status.textContent = message;
      }
    }
  );
}
